import VisibilityIcon from "@mui/icons-material/Visibility";
import {IconButton, Stack, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import {ColumnDef} from "@tanstack/react-table";
import type {AggregationSurveySubmissionListItem}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {SurveyAggregationService} from "crqlar-react-common/dist/services/aggregation/surveyAggregation.service";
import {SurveyService} from "crqlar-react-common/dist/services/survey/survey.service";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useMemo, useState} from "react";
import {useQuery} from "react-query";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    useServerTable
} from "../../common/serverTable";
import {DateElement} from "../../common/uiElements/DateElement";
import {SurveyReviewStateElement} from "../../common/uiElements/SurveyReviewStateElement";
import {SURVEY_GUEST_SUBMISSIONS_TABLE_ID} from "../shared/surveyQueryRefresh";
import SurveySubmissionDialog from "../submissions/SurveySubmissionDialog";

interface Props {
    reservationId?: string
    profileId?: string
}

/** Five rows keep both share-dialog tables, and the link area above them, free of an inner scrollbar. */
const PAGE_SIZE = 5;

/**
 * A guest's submissions inside the share dialog: the real table, in the `embedded` preset -
 * no toolbar, fixed columns, fixed page size, and the framework's own pagination footer.
 *
 * The `tableId` is shared with the full submissions history on purpose. It is what puts this
 * table's query key under the same prefix `useSurveyReviewRefresh()` invalidates, and the
 * embedded preset is what makes sharing it safe: no column preferences are stored under it.
 */
const SurveyShareSubmissionsTable = ({reservationId, profileId}: Props) => {
    const {t} = useLocalize();
    const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);

    const columns = useMemo<ColumnDef<AggregationSurveySubmissionListItem>[]>(() => [
        {
            id: 'surveyName',
            header: t('Survey'),
            accessorFn: submission => submission.surveyName,
            cell: ({row}) => (
                <Typography variant={'body1'} noWrap>{row.original.surveyName || '—'}</Typography>
            ),
            size: 240,
            meta: {grow: true}
        },
        {
            id: 'createdAt',
            header: t('Submitted'),
            accessorFn: submission => submission.createdAt,
            cell: ({row}) => <DateElement date={row.original.createdAt}/>,
            size: 170
        },
        {
            id: 'reviewState',
            header: t('Review state'),
            accessorFn: submission => submission.reviewState,
            cell: ({row}) => <SurveyReviewStateElement reviewState={row.original.reviewState}/>,
            size: 190
        },
        {
            id: 'actions',
            header: t('Actions'),
            cell: ({row}) => (
                <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'} data-no-row-click>
                    <Tooltip title={t('View submission')}>
                        <IconButton size={'small'} onClick={() => setSelectedSubmissionId(row.original.id)}>
                            <VisibilityIcon fontSize={'small'}/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
            size: 72,
            meta: {align: 'right'}
        }
    ], [t]);

    const controller = useServerTable<AggregationSurveySubmissionListItem>({
        tableId: SURVEY_GUEST_SUBMISSIONS_TABLE_ID,
        presentation: 'embedded',
        columns,
        getRowId: submission => submission.id,
        onRowClick: submission => setSelectedSubmissionId(submission.id),
        defaultPageSize: PAGE_SIZE,
        enabled: !!profileId || !!reservationId,
        queryDependencies: [profileId, reservationId],
        // Newest first, fixed: sorting here rather than through `initialSorting` keeps the
        // header free of the sort and remove-sort controls this dialog has no room for.
        queryFn: ({pagination}) => SurveyAggregationService.listSubmissions({
            profileIds: profileId ? [profileId] : undefined,
            reservationIds: reservationId ? [reservationId] : undefined,
            limit: {
                offset: pagination.pageIndex * pagination.pageSize,
                limit: pagination.pageSize,
                sort: [{field: 'createdAt', direction: 'DESC'}]
            }
        })
    });

    const submissions = controller.query.data?.content ?? [];
    const selectedSubmission = submissions.find(submission => submission.id === selectedSubmissionId);
    const dialogSurveyId = selectedSubmission?.surveyId;
    const dialogSurveyQuery = useQuery(
        ['survey', dialogSurveyId],
        () => SurveyService.getSurveyById(dialogSurveyId!),
        {enabled: !!dialogSurveyId, refetchOnWindowFocus: false}
    );

    return (
        <>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>

            {!!selectedSubmission && (
                <SurveySubmissionDialog
                    open
                    onClose={() => setSelectedSubmissionId(null)}
                    submission={selectedSubmission}
                    survey={dialogSurveyQuery.data}
                />
            )}
        </>
    );
};

export default SurveyShareSubmissionsTable;
